import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API, C } from '../../constants';
import { getInitials, getAvatarColor, timeAgo } from '../../utils';
import { subscribeSSE } from '../../sse';
import useWindowSize from '../../hooks/useWindowSize';
import AdminChatArea from '../chat/AdminChatArea';
import ConversationList from '../chat/ConversationList';

export default function AdminInboxTab({ token }) {
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser]   = useState(null);
  const [messageCache, setMessageCache]   = useState({});
  const [lastIDCache, setLastIDCache]     = useState({});
  const [presenceList, setPresenceList]   = useState([]);
  const [view, setView]                   = useState('conversations');
  const { width }        = useWindowSize();
  const isMobile         = width < 700;
  const selectedUserRef  = useRef(null);
  const loadConvsRef     = useRef(null);
  const fetchMsgsRef     = useRef(null);
  useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/chat/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setConversations(Array.isArray(d) ? d : []); }
    } catch {}
  }, [token]);

  const loadPresence = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/presence`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPresenceList(Array.isArray(d) ? d : []); }
    } catch {}
  }, [token]);

  const fetchUserMessages = useCallback(async (userID) => {
    const since = lastIDCache[userID] || 0;
    try {
      const res = await fetch(`${API}/api/chat/messages?user_id=${userID}&since=${since}&limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      if (!data || data.length === 0) return;
      const newLastID = data[data.length - 1].id;
      setLastIDCache(prev => ({ ...prev, [userID]: newLastID }));
      setMessageCache(prev => {
        const existing = prev[userID] || [];
        const existingIDs = new Set(existing.map(m => m.id));
        const newMsgs = data.filter(m => !existingIDs.has(m.id));
        if (newMsgs.length === 0) return prev;
        return { ...prev, [userID]: [...existing, ...newMsgs] };
      });
    } catch {}
  }, [token, lastIDCache]);

  useEffect(() => { loadConvsRef.current = loadConversations; }, [loadConversations]);
  useEffect(() => { fetchMsgsRef.current = fetchUserMessages; }, [fetchUserMessages]);

  useEffect(() => { loadConversations(); loadPresence(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // La presencia no llega por SSE: mientras se ve la lista se refresca sola.
  useEffect(() => {
    if (view !== 'presence') return;
    const id = setInterval(loadPresence, 30000);
    return () => clearInterval(id);
  }, [view, loadPresence]);

  useEffect(() => {
    if (!selectedUser) return;
    const uid = selectedUser.user_id;
    // El SSE solo refresca la conversación abierta: si ya había caché, pueden
    // haber llegado mensajes mientras estaba en otra; se piden los nuevos.
    if (messageCache[uid]) { fetchMsgsRef.current?.(uid); return; }
    fetch(`${API}/api/chat/messages?user_id=${uid}&since=0&limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(data => {
        if (!data || data.length === 0) return;
        setLastIDCache(prev => ({ ...prev, [uid]: data[data.length-1].id }));
        setMessageCache(prev => ({ ...prev, [uid]: data }));
      }).catch(() => {});
  }, [selectedUser?.user_id, token]);

  useEffect(() => {
    if (!token) return;
    return subscribeSSE(token, data => {
      try {
        if (data.type === 'connected') { loadConvsRef.current?.(); return; }
        if (data.type === 'messages_read') {
          const uid = data.user_id;
          setMessageCache(prev => {
            if (!prev[uid]) return prev;
            return { ...prev, [uid]: prev[uid].map(m => m.sender_role === 'admin' ? { ...m, read: true } : m) };
          });
          return;
        }
        if (data.type !== 'message') return;
        loadConvsRef.current?.();
        const cur = selectedUserRef.current;
        if (cur && (data.sender_id === cur.user_id || data.receiver_id === cur.user_id)) {
          fetchMsgsRef.current?.(cur.user_id);
        }
      } catch {}
    });
  }, [token]);

  const selectUser = (conv) => {
    setSelectedUser(conv); setView('chat');
    setConversations(prev => prev.map(c => c.user_id === conv.user_id ? { ...c, unread_count: 0 } : c));
    fetch(`${API}/admin/chat/read?user_id=${conv.user_id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleNewMessage = useCallback(() => {
    if (selectedUserRef.current) fetchUserMessages(selectedUserRef.current.user_id);
  }, [fetchUserMessages]);

  const totalUnread    = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
  const onlineCount    = presenceList.filter(p => p.is_online).length;
  const activeMessages = selectedUser ? (messageCache[selectedUser.user_id] || []) : [];
  const tabStyle = (active) => ({ padding:'8px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'500', background: active ? `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})` : C.accentDim, color: active ? '#fff' : C.textMuted, display:'flex', alignItems:'center', gap:'5px', whiteSpace:'nowrap' });

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', minHeight:0 }}>
      <div style={{ display:'flex', gap:'6px', padding:'0 0 16px 0', borderBottom:`1px solid ${C.border}`, marginBottom:'12px', flexWrap:'wrap' }}>
        <button style={tabStyle(view === 'conversations' || view === 'chat')} onClick={() => setView(selectedUser ? 'chat' : 'conversations')}>
          💬 Mensajes {totalUnread > 0 && <span style={{ background:'#e74c3c', color:'#fff', borderRadius:'10px', padding:'1px 6px', fontSize:'10px' }}>{totalUnread}</span>}
        </button>
        <button style={tabStyle(view === 'presence')} onClick={() => { setView('presence'); loadPresence(); }}>
          👥 En línea <span style={{ color: view === 'presence' ? 'rgba(255,255,255,0.8)' : C.online, fontSize:'11px' }}>({onlineCount})</span>
        </button>
      </div>
      {view === 'presence' && (
        <div style={{ flex:1, overflowY:'auto' }}>
          <p style={{ color:C.textMuted, fontSize:'12px', marginBottom:'16px', padding:'0 16px' }}><span style={{ color:C.online }}>●</span> {onlineCount} en línea · {presenceList.length} total</p>
          <div style={{ display:'flex', flexDirection:'column', gap:'8px', padding:'0 16px' }}>
            {presenceList.map(p => {
              const av = getAvatarColor(p.user_name);
              return (
                <div key={p.user_id} style={{ padding:'12px 14px', background:C.bgCard, borderRadius:'10px', border:`1px solid ${p.is_online ? 'rgba(46,204,113,0.25)' : C.border}`, display:'flex', alignItems:'center', gap:'10px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:av.bg, color:av.text, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'600', flexShrink:0 }}>{getInitials(p.user_name)}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <p style={{ color:C.text, fontSize:'13px', fontWeight:'500' }}>{p.user_name}</p>
                      <span style={{ width:'7px', height:'7px', borderRadius:'50%', background: p.is_online ? C.online : C.textDim, flexShrink:0 }} />
                    </div>
                    <p style={{ color:C.textMuted, fontSize:'11px', marginTop:'1px' }}>{p.user_email}</p>
                    {p.is_online && p.watching_now && <p style={{ color:C.accentSoft, fontSize:'11px', marginTop:'3px' }}>▶ {p.watching_now.replace(/\.[^.]+$/, '')}</p>}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    {p.is_online ? <span style={{ background:C.onlineDim, color:C.online, fontSize:'10px', padding:'2px 8px', borderRadius:'20px', border:'1px solid rgba(46,204,113,0.3)' }}>En línea</span> : <span style={{ color:C.textDim, fontSize:'11px' }}>{timeAgo(p.last_seen)}</span>}
                    {p.is_online && p.session_mins > 0 && <p style={{ color:C.textDim, fontSize:'10px', marginTop:'3px' }}>{p.session_mins}m sesión</p>}
                  </div>
                </div>
              );
            })}
            {presenceList.length === 0 && <p style={{ color:C.textDim, fontSize:'13px', textAlign:'center', padding:'24px 0' }}>Sin datos de presencia aún</p>}
          </div>
        </div>
      )}
      {(view === 'conversations' || view === 'chat') && (
        isMobile ? (
          <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
            {view === 'conversations'
              ? <ConversationList conversations={conversations} selectedUser={selectedUser} onSelect={selectUser} />
              : <AdminChatArea key={selectedUser?.user_id} token={token} selectedUser={selectedUser} messages={activeMessages} isMobile={isMobile} onBack={() => setView('conversations')} onNewMessage={handleNewMessage} />
            }
          </div>
        ) : (
          <div style={{ flex:1, display:'flex', minHeight:0, border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden' }}>
            <div style={{ width:'240px', borderRight:`1px solid ${C.border}`, overflowY:'auto', flexShrink:0, scrollbarWidth:'thin', scrollbarColor:`${C.accentBorder} transparent` }}>
              <ConversationList conversations={conversations} selectedUser={selectedUser} onSelect={selectUser} />
            </div>
            <AdminChatArea key={selectedUser?.user_id} token={token} selectedUser={selectedUser} messages={activeMessages} isMobile={isMobile} onBack={() => setView('conversations')} onNewMessage={handleNewMessage} />
          </div>
        )
      )}
    </div>
  );
}
